import { useCallback, useMemo, useState } from 'react';
import { createResolveUsdConversion } from './usdConversionDisplay';
const UNAVAILABLE_MSG = 'Related transaction is unavailable or outside your permission.';
const initialLedgerDetailState = {
    creditDetail: null,
    usdDetail: null,
    relatedLoading: null,
};
export function usePortalLedgerDetailState(options = {}) {
    const { fetchCreditById, fetchUsdById, onUnavailable, creditRows = [], usdRows = [] } = options;
    const [state, setState] = useState(initialLedgerDetailState);
    const notifyUnavailable = useCallback(() => {
        onUnavailable === null || onUnavailable === void 0 ? void 0 : onUnavailable();
    }, [onUnavailable]);
    const openCredit = useCallback((entry) => {
        setState({ creditDetail: entry, usdDetail: null, relatedLoading: null });
    }, []);
    const openUsd = useCallback((row) => {
        setState({ creditDetail: null, usdDetail: row, relatedLoading: null });
    }, []);
    const openCreditById = useCallback(async (id) => {
        const local = creditRows.find((r) => String(r.id) === id);
        if (local) {
            openCredit(local);
            return;
        }
        if (!fetchCreditById) {
            notifyUnavailable();
            return;
        }
        setState((prev) => ({ ...prev, relatedLoading: 'credit' }));
        try {
            const row = await fetchCreditById(id);
            if (!row) {
                notifyUnavailable();
                setState((prev) => ({ ...prev, relatedLoading: null }));
                return;
            }
            openCredit(row);
        }
        catch {
            notifyUnavailable();
            setState((prev) => ({ ...prev, relatedLoading: null }));
        }
    }, [creditRows, fetchCreditById, openCredit, notifyUnavailable]);
    const openUsdById = useCallback(async (id) => {
        const local = usdRows.find((r) => String(r.id) === id);
        if (!fetchUsdById) {
            if (local) {
                openUsd(local);
                return;
            }
            notifyUnavailable();
            return;
        }
        if (local) {
            openUsd(local);
        }
        else {
            setState((prev) => ({ ...prev, relatedLoading: 'usd' }));
        }
        try {
            const row = await fetchUsdById(id);
            if (!row) {
                if (!local)
                    notifyUnavailable();
                return;
            }
            openUsd(row);
        }
        catch {
            if (!local)
                notifyUnavailable();
        }
        finally {
            setState((prev) => ({ ...prev, relatedLoading: null }));
        }
    }, [usdRows, fetchUsdById, openUsd, notifyUnavailable]);
    const closeCredit = useCallback(() => setState((prev) => ({ ...prev, creditDetail: null, relatedLoading: null })), []);
    const closeUsd = useCallback(() => setState((prev) => ({ ...prev, usdDetail: null, relatedLoading: null })), []);
    const handleOpenUsdSource = useCallback((usdId) => void openUsdById(usdId), [openUsdById]);
    const handleOpenCreditSource = useCallback((creditId) => void openCreditById(creditId), [openCreditById]);
    const resolveUsdConversion = useMemo(() => {
        if (!fetchUsdById)
            return undefined;
        return createResolveUsdConversion(async (id) => {
            try {
                return await fetchUsdById(id);
            }
            catch {
                return null;
            }
        });
    }, [fetchUsdById]);
    return {
        creditDetail: state.creditDetail,
        usdDetail: state.usdDetail,
        relatedLoading: state.relatedLoading,
        openCredit,
        openUsd,
        openCreditById,
        openUsdById,
        closeCredit,
        closeUsd,
        handleOpenUsdSource,
        handleOpenCreditSource,
        resolveUsdConversion,
        unavailableMessage: UNAVAILABLE_MSG,
    };
}
